import { getCountryCodeByName } from '@heidi/utils/countries';
import { parseDateAsLocal } from '@heidi/utils/dates';
import { useQueryUser } from '@heidi/workspace/data';
import { format, isValid, parse } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { Country } from 'react-phone-number-input';

import { GENDER_OPTIONS, PATIENT_PHONE_COUNTRIES } from '../../contracts';
import { useUserDateFormat } from '../hooks/useUserDateFormat';
import { capitalize, isFutureDob } from './identity';

const PHONE_COUNTRIES = PATIENT_PHONE_COUNTRIES as string[] as Country[];

const phoneCountriesByLocale = new Map<string, Country[]>();

const sortedPhoneCountries = (locale: string): Country[] => {
  let sorted = phoneCountriesByLocale.get(locale);
  if (!sorted) {
    const names = new Intl.DisplayNames([locale], { type: 'region', fallback: 'code' });
    const nameOf = (code: Country) => names.of(code) ?? code;
    sorted = [...PHONE_COUNTRIES].sort((a, b) =>
      nameOf(a).localeCompare(nameOf(b), locale)
    );
    phoneCountriesByLocale.set(locale, sorted);
  }
  return sorted;
};

const toPhoneCountry = (name?: string | null): Country | undefined => {
  const code = getCountryCodeByName(name);
  return PHONE_COUNTRIES.find((c) => c === code);
};

export interface IdentityEditorIntl {
  dobLabel: string;
  genderLabel: string;
  identifierLabel: string;
  dobPlaceholder: string;
  genderPlaceholder: string;
  identifierPlaceholder: string;
  phonePlaceholder: string;
  /** Default country for the phone-number input's flag selector. Falls back to
   *  AU if the user's `country` doesn't resolve to a picker country. */
  phoneDefaultCountry: Country;
  /** Countries the phone picker offers: the user's own country first, then the
   *  rest sorted by their name in the UI locale. */
  phoneCountries: Country[];
  ageFormat: (age: number) => string;
  formatDob: (iso: string) => string;
  /** Normalises a user-typed date string back to ISO `yyyy-MM-dd`. Accepts
   *  both ISO format and the user's display format. Returns `null` if the
   *  input cannot be parsed so the caller can show an inline validation
   *  error instead of round-tripping garbage through the backend. Empty
   *  input returns `''` to signal "clear the field". */
  parseDob: (input: string) => string | null;
  /** Inline error message shown when a date of birth is in the future. */
  dobFutureMessage: string;
  /** Returns `dobFutureMessage` when the (already-parsed ISO) date is in the
   *  future, else `null`. Designed as a second-stage check after `parseDob`. */
  validateDobNotFuture: (iso: string) => string | null;
  genderOptions: Array<{ value: string; label: string }>;
  formatGender: (value: string) => string;
}

/** Shared intl + formatters for the identity-block inline editors, used by
 *  both the drawer and the full-page view. */
export const useIdentityEditorIntl = (): IdentityEditorIntl => {
  const intl = useIntl();
  const { data: userData } = useQueryUser((data) => ({
    country: data?.country,
  }));
  const phoneDefaultCountry: Country = toPhoneCountry(userData?.country) ?? 'AU';
  const locale = intl.locale || 'en';
  const phoneCountries = useMemo(
    () => [
      phoneDefaultCountry,
      ...sortedPhoneCountries(locale).filter((c) => c !== phoneDefaultCountry),
    ],
    [locale, phoneDefaultCountry]
  );
  // Same read the app spelled inline with `DEFAULT_DATE_FORMAT` from
  // `@/components/constants`: that barrel is app-side, and its value is the
  // schema enum member this hook already falls back to.
  const dateFormat = useUserDateFormat();

  const dobFutureMessage = intl.formatMessage({
    defaultMessage: 'Date of birth cannot be in the future',
    id: 't1NrC9HdS3',
  });

  const genderOptionLabels: Record<string, string> = {
    male: intl.formatMessage({ defaultMessage: 'Male', id: 'jIbAkyUeIY' }),
    female: intl.formatMessage({ defaultMessage: 'Female', id: '74BYXLpq8f' }),
    other: intl.formatMessage({ defaultMessage: 'Other', id: '/VnDMl81rh' }),
    unknown: intl.formatMessage({ defaultMessage: 'Unknown', id: '5jeq8PJZg7' }),
  };

  return {
    dobLabel: intl.formatMessage({ defaultMessage: 'Date of birth', id: 'GFTdXwO9W3' }),
    genderLabel: intl.formatMessage({ defaultMessage: 'Gender', id: 'm8/n8cq9i9' }),
    identifierLabel: intl.formatMessage({
      defaultMessage: 'Patient identifier',
      id: 'aWv7G4OvhI',
    }),
    dobPlaceholder: dateFormat.toLowerCase(),
    // Not a country example: the picker's selected country can differ from the
    // user's, and an example from the wrong country reads as an error.
    phonePlaceholder: intl.formatMessage({
      defaultMessage: 'Enter a phone number',
      id: '1+vVM2/+Iy',
      description: 'Placeholder for the patient phone number field',
    }),
    genderPlaceholder: intl.formatMessage({
      defaultMessage: 'Add gender',
      id: '9tAlT3A5zc',
    }),
    phoneDefaultCountry,
    phoneCountries,
    identifierPlaceholder: intl.formatMessage({
      defaultMessage: 'e.g Jane Doe 27F',
      id: 'nSrztg+yEp',
    }),
    ageFormat: (age: number) =>
      intl.formatMessage({ defaultMessage: '{age} yrs', id: 'GFeWiJa6zD' }, { age }),
    formatDob: (iso: string): string => {
      const parsed = parseDateAsLocal(iso);
      return parsed ? format(parsed, dateFormat) : iso;
    },
    parseDob: (input: string): string | null => {
      if (input === '') return '';
      const fromIso = parseDateAsLocal(input);
      if (fromIso) return format(fromIso, 'yyyy-MM-dd');
      const fromDisplay = parse(input, dateFormat, new Date());
      if (isValid(fromDisplay)) return format(fromDisplay, 'yyyy-MM-dd');
      return null;
    },
    dobFutureMessage,
    validateDobNotFuture: (iso: string): string | null =>
      isFutureDob(iso) ? dobFutureMessage : null,
    genderOptions: GENDER_OPTIONS.map((o) => ({
      value: o.value,
      label: genderOptionLabels[o.value] ?? o.label,
    })),
    formatGender: (value: string) => genderOptionLabels[value] ?? capitalize(value),
  };
};
